/**
 * The device card's shapes and its two display rules (settings part 2a, 2026-10-11).
 *
 * Moved verbatim out of src/components/common/NotificationPermissionCard.tsx with the card's behaviour, which now lives
 * in `useNotificationPermission` (./useNotificationPermission.ts). What a registered device IS (its row) and how a row
 * is named (its os and browser in words, the date it was last seen) are read by today's card and by the new design's
 * settings page alike, so the two can never name a device differently.
 *
 * Nothing here imports anything: no service, no React, nothing of today's design.
 */

/** One device registered for the account's notifications, as the card lists it. */
export interface NotificationDeviceRow {
  token: string;
  platform?: string;
  os?: string;
  browser?: string;
  standalone?: boolean;
  lastSeen?: Date | null;
  isThisDevice: boolean;
}

/** A device's name: its os in words, the browser when it is a known one, and whether it is the installed app. */
export function deviceLabel(d: NotificationDeviceRow): string {
  const os = (d.os || '').trim();
  const browser = (d.browser || '').trim();
  const osLabel = os === 'ios' ? 'iphone / ipad' : os === 'macos' ? 'mac' : os || 'device';
  const via = browser && browser !== 'other' ? ` · ${browser}` : '';
  const installed = d.standalone ? ' · installed app' : '';
  return `${osLabel}${via}${installed}`;
}

/** When a device was last seen, in the browser's own date words; nothing for a date that cannot be read. */
export function lastSeenLabel(d: Date | null | undefined): string {
  if (!d || Number.isNaN(d.getTime())) return '';
  try {
    return `last seen ${d.toLocaleDateString()}`;
  } catch {
    return '';
  }
}
